// src/Toast.js

import React, { createContext, useContext, useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ToastContext = createContext(null);

const TOAST_STYLES = {
  success:  { bar: 'bg-green-500',  icon: '✓', iconBg: 'bg-green-100  dark:bg-green-900/30  text-green-600  dark:text-green-400'  },
  error:    { bar: 'bg-red-500',    icon: '✕', iconBg: 'bg-red-100    dark:bg-red-900/30    text-red-600    dark:text-red-400'    },
  info:     { bar: 'bg-purple-500', icon: '✦', iconBg: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
  warning:  { bar: 'bg-yellow-500', icon: '⚠', iconBg: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' },
  download: { bar: 'bg-indigo-500', icon: '↓', iconBg: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' },
};

const ToastItem = memo(({ id, type, title, message, duration, onDismiss }) => {
  const style = TOAST_STYLES[type] || TOAST_STYLES.info;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.92 }}
      animate={{ opacity: 1, x: 0,  scale: 1    }}
      exit={{    opacity: 0, x: 60, scale: 0.92  }}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative flex items-start gap-3 w-80 bg-white dark:bg-[#1a1a2e] border border-gray-100 dark:border-purple-500/15 rounded-2xl px-4 py-3 shadow-lg dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)] overflow-hidden"
    >
      <motion.div
        className={"absolute bottom-0 left-0 h-0.5 " + style.bar}
        initial={{ width: '100%' }}
        animate={{ width: '0%'   }}
        transition={{ duration: duration / 1000, ease: 'linear' }}
      />
      <div className={"w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold " + style.iconBg}>
        {style.icon}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{title}</p>
        {message && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">{message}</p>}
      </div>
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 transition-colors duration-150 mt-0.5 text-lg leading-none"
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </motion.div>
  );
});
ToastItem.displayName = 'ToastItem';

const ToastContainer = memo(({ toasts, onDismiss }) => (
  <div
    aria-live="polite"
    aria-atomic="false"
    className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 pointer-events-none"
  >
    <AnimatePresence mode="sync">
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem {...t} onDismiss={onDismiss} />
        </div>
      ))}
    </AnimatePresence>
  </div>
));
ToastContainer.displayName = 'ToastContainer';

export const ToastProvider = memo(({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((type, title, message, duration = 3500) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-4), { id, type, title, message, duration }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration + 300);
  }, []);

  const success  = useCallback((title, msg, dur) => toast('success',  title, msg, dur), [toast]);
  const error    = useCallback((title, msg, dur) => toast('error',    title, msg, dur), [toast]);
  const info     = useCallback((title, msg, dur) => toast('info',     title, msg, dur), [toast]);
  const warning  = useCallback((title, msg, dur) => toast('warning',  title, msg, dur), [toast]);
  const download = useCallback((title, msg, dur) => toast('download', title, msg, dur), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, info, warning, download }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
});
ToastProvider.displayName = 'ToastProvider';

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};